import React, { FC, useMemo, useRef, useState } from 'react';
import { AvDialog, AvMessagePopover, AvTooltip, CopyToClipboard, CopyType, Initials, Select, TextInput } from '@fabric-ui/components';
import { Delete, Export, QuestionFull, Web, X } from '@fabric-ui/icons';
import { AlertsFull, AssetsFull, FindingsFull, IdentitiesFull, IncidentsFull, Risk360Full, ThreatsFull } from '@fabric-ui/icons/app-icons';
import { WarningFullOrange } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { difference, iconSize, intersection, noop, uniqBy } from '@fabric-ui/utils';
import { Box, Button, Divider, IconButton, Popover, SxProps, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../../../context/AvContextProvider';
import { useAllApps } from '../../../hooks/useAllApps';
import { useGetUsersInfo } from '../../../hooks/useGetUsersInfo';
import { APP_PATHS, getRoleNameByValue, PAGE_PATHS, Permission, ROLES } from '../../../types';
import { formDisabledStyle, isNullOrUndefined } from '../../../utils/utils';
import { useGetAllDashboardAccessRoles } from '../../AccountManagement/hooks';
import { ModeType } from '../constants';
import { useSharableReportUsers } from '../hooks';
import { useDashboardsTagsList } from '../hooks/useDashboardsTagsList';
import { CustomDashboardDto, DashboardReportPermission, PermissionType, publicId } from '../types';
import { DashboardsTagsSelect } from './DashboardsTagsSelect';

const exitIcon = <X style={iconSize(26)} />;
const deleteIcon = <Delete style={iconSize(18)} />;
const exportIcon = (size = 14, iconStyle = {}) => <Export style={{ ...iconSize(size), ...iconStyle }} />;
const orangeWarningIcon = <WarningFullOrange />;

const iconComponents = {
  VULNERABILITIES: FindingsFull,
  DETECTIONS: AlertsFull,
  INCIDENTS: IncidentsFull,
  RISK360: Risk360Full,
  ASSETS: AssetsFull,
  THREATS: ThreatsFull,
  IDENTITIES: IdentitiesFull,
};

interface EditDashboardDetailsWindowProps {
  title?: string;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  anchorEl?: Element;
  setAnchorEl?: React.Dispatch<React.SetStateAction<Element | undefined>>;
  initData: Partial<CustomDashboardDto>;
  onSuccess: (dashboard: Partial<CustomDashboardDto>) => void;
  allowImportContent?: boolean;
  disabled?: boolean;
  onSuccessSaveDashboard?: boolean;
  modeType?: ModeType;
  buttonText?: string;
  warning?: string;
  disableBackdropClick?: boolean;
}

const PublicOption = { value: publicId, title: 'Public', icon: <Web style={iconSize(24)} /> };
const EditDashboardDetailsWindow: FC<EditDashboardDetailsWindowProps> = ({
  title = 'Dashboard',
  anchorEl,
  setAnchorEl = noop,
  isOpen = false,
  setIsOpen = noop,
  initData,
  onSuccess,
  allowImportContent = false,
  disabled = false,
  onSuccessSaveDashboard = false,
  modeType = ModeType.Create,
  buttonText,
  warning,
  disableBackdropClick = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const initialUsersOfDashboard = useMemo(
    () =>
      [...(initData.dashboardReportPermission?.editors || []), ...(initData.dashboardReportPermission?.viewers || [])].filter(
        user => user !== publicId
      ),
    []
  );
  const onClose = (event?, reason?) => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    setAnchorEl(undefined);
    setIsOpen(false);
  };

  const [newDashboardData, setNewDashboardData] = useState<Partial<CustomDashboardDto>>(initData);
  const {
    userPermissions: { hasAllowedPermission },
    enabledApps,
  } = useAvContext();
  const [attachedFileName, setAttachedFileName] = useState<string | undefined>(undefined);
  const fileInputRef = useRef<any>(undefined);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();

      reader.onload = event => {
        const result = event.target?.result;
        try {
          if (result && typeof result === 'string') {
            const jsonData = JSON.parse(result);
            setAttachedFileName(file.name);
            onChange('widgets')(jsonData);
          } else {
            enqueueSnackbar('Unable to parse JSON', { variant: 'error' });
          }
        } catch {
          enqueueSnackbar('Unable to parse JSON', { variant: 'error' });
        } finally {
          fileInputRef.current.value = '';
        }
      };

      reader.readAsText(file);
    }
  };

  const handleAttachmentClick = () => fileInputRef.current.click();
  const { data: AllApps = [] } = useAllApps();
  const isAllowedShareableUsers = hasAllowedPermission({ path: PAGE_PATHS.REPORTS, permission: Permission.UPDATE });
  const { data: accountDashboardAccessRoles = [] } = useGetAllDashboardAccessRoles();
  const { data: users = [], isLoading: isLoadingAllUsers } = useSharableReportUsers(isAllowedShareableUsers);
  const { data: dashboardTags = [] } = useDashboardsTagsList();
  const { usersInfo: currentDashboardUsers, isLoading: isLoadingUsers } = useGetUsersInfo(
    uniqBy(isAllowedShareableUsers ? [] : initialUsersOfDashboard, v => v),
    data => data,
    []
  );
  const isLoading = isLoadingAllUsers || isLoadingUsers;

  const usersOptionsForEditors = useMemo(
    () => [
      { value: publicId, title: 'Public', iconFull: <Web style={iconSize(24)} /> },
      ...(isAllowedShareableUsers ? users : currentDashboardUsers).map(({ id, firstName, lastName }) => ({
        value: id,
        title: `${firstName} ${lastName}`,
      })),
    ],
    [users, currentDashboardUsers, isAllowedShareableUsers]
  );
  const allowedToViewUsersSet = useMemo(
    () => new Set(newDashboardData.dashboardReportPermission?.viewers || []),
    [newDashboardData.dashboardReportPermission?.viewers]
  );

  const allowedToEditUsersSet = useMemo(
    () => new Set(newDashboardData.dashboardReportPermission?.editors || []),
    [newDashboardData.dashboardReportPermission?.editors]
  );

  const usersOptionsForViewers = useMemo(
    () => usersOptionsForEditors.map(option => ({ ...option, disabled: allowedToEditUsersSet.has(option.value) })),
    [usersOptionsForEditors, newDashboardData.dashboardReportPermission?.editors]
  );

  const filteredAppsOption = enabledApps.map(({ id, name, backgroundColor, enumValue }) => {
    const IconComponent = iconComponents[enumValue];
    return {
      value: AllApps.find(({ name }) => id === APP_PATHS[name])?.name,
      title: name,
      iconFull: IconComponent ? <IconComponent /> : undefined,
      backgroundColor,
    };
  });

  const tagsOptions = useMemo(() => dashboardTags.map(tag => ({ value: tag, title: tag })), [dashboardTags]);

  const onSave = () => {
    if (onSuccessSaveDashboard) {
      return;
    }
    onSuccess(newDashboardData);
    onClose();
  };

  const getPermissionType = (newDashboardReportPermission: DashboardReportPermission) => {
    if (newDashboardReportPermission.editors.includes(publicId)) {
      return PermissionType.publicEdit;
    }
    if (newDashboardReportPermission.viewers.includes(publicId)) {
      return PermissionType.publicView;
    }
    if (newDashboardReportPermission.editors.length || newDashboardReportPermission.viewers.length) {
      return PermissionType.shared;
    }
    return PermissionType.private;
  };

  const onChange = field => value => {
    const newData = { ...newDashboardData, [field]: value };
    setNewDashboardData(newData);
    if (anchorEl && (field !== 'name' || value.length)) {
      onSuccess(newData);
    }
  };
  const getViewersList = (viewers, newEditorsList) => {
    const newEditorsSet = new Set(newEditorsList);
    const newEditorRemoved = difference(allowedToEditUsersSet, newEditorsSet).values().next().value;
    if (newEditorRemoved) {
      return [...viewers, newEditorRemoved];
    }
    const newEditorAdded = intersection(newEditorsSet, allowedToViewUsersSet).values().next().value;
    if (newEditorAdded && allowedToViewUsersSet.has(newEditorAdded as string)) {
      return viewers.filter(v => v !== newEditorAdded);
    }
    return viewers;
  };
  const renderFileInput = <input accept=".json" ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} />;
  const isViewersDisabled = newDashboardData.dashboardReportPermission?.editors?.includes(publicId);
  const onChangeDashboardReportPermission = (field: 'viewers' | 'editors') => (value: string[]) => {
    if (newDashboardData.dashboardReportPermission) {
      const { viewers } = newDashboardData.dashboardReportPermission;
      const newDashboardReportPermission: DashboardReportPermission = {
        ...newDashboardData.dashboardReportPermission,
        [field]: value,
        ...(field === 'editors' && {
          viewers: value.includes(publicId) || viewers.includes(publicId) ? [publicId] : getViewersList(viewers, value),
        }),
      };

      const newValue = {
        ...newDashboardData,
        dashboardReportPermission: {
          ...newDashboardReportPermission,
          type: getPermissionType(newDashboardReportPermission),
        },
      };

      setNewDashboardData(newValue);
      if (anchorEl) {
        onSuccess(newValue);
      }
    }
  };

  const viewersValue = useMemo(
    () =>
      allowedToViewUsersSet.has(publicId)
        ? newDashboardData!.dashboardReportPermission!.viewers
        : [
            ...(newDashboardData!.dashboardReportPermission?.viewers ?? []),
            ...(newDashboardData!.dashboardReportPermission?.editors.filter(v => !allowedToViewUsersSet.has(v)) ?? []),
          ],
    [allowedToViewUsersSet, newDashboardData!.dashboardReportPermission?.editors]
  );

  const commonProps = fieldName =>
    ({
      size: 'medium',
      value: newDashboardData[fieldName],
      onChange: onChange(fieldName),
    }) as any;

  const selectCommonStyle = {
    '&.autocomplete-multiple': {
      '&, & .MuiFormControl-root': { maxWidth: '100%' },
      '.MuiOutlinedInput-root.MuiAutocomplete-inputRoot': { py: '5px' },
    },
  };

  const renderOption = ({
    title,
    iconFull = <Initials name={title} size="xSmall" />,
    sx,
  }: {
    title: string;
    iconFull: React.JSX.Element;
    sx?: SxProps;
  }) => (
    <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
      <Box sx={{ ...sx, borderRadius: '8px', ...flex.center }}>{iconFull}</Box>
      {title}
    </Box>
  );

  const TitleGenerator = {
    Edit: `Edit ${title}`,
    SaveAs: `Save as a New ${title}`,
    Share: `Share ${title}`,
    Create: `Create New ${title}`,
  };

  const ButtonGenerator = {
    Edit: 'Save',
    SaveAs: 'Create',
    Share: 'Done',
    Create: 'Create',
  };

  const isSaveDisabled = !newDashboardData.name && modeType !== ModeType.Share;

  const removeFileInput = () => {
    setAttachedFileName(undefined);
    onChange('widgets')([]);
  };

  const disabledStyle = disabled ? formDisabledStyle : {};

  const inputs = (
    <Box sx={{ gap: 2, ...flex.col, ...disabledStyle, mb: 3 }}>
      {allowImportContent && renderFileInput}
      {attachedFileName && (
        <>
          <Box sx={{ ...flex.justifyBetweenCenter }}>
            <Box sx={{ ...flex.row, gap: 1 }}>
              <Box sx={{ color: 'var(--color-content-base-secondary)' }}>Attached template file:</Box>
              <Box sx={{ color: 'var(--color-content-base-primary)' }}>{attachedFileName}</Box>
            </Box>
            <Box sx={{ gap: 2, ...flex.row }}>
              <AvTooltip title="Replace Template File">
                <IconButton onClick={handleAttachmentClick}>{exportIcon(18)}</IconButton>
              </AvTooltip>
              <AvTooltip title="Delete File">
                <IconButton onClick={removeFileInput}>{deleteIcon}</IconButton>
              </AvTooltip>
            </Box>
          </Box>
          <Divider />
        </>
      )}
      {!isNullOrUndefined(newDashboardData.name) && (
        <TextInput
          {...commonProps('name')}
          label="Name"
          sx={{ maxWidth: '100%' }}
          isRequired
          autoFocus={modeType === ModeType.Edit}
          disableClearButton
        />
      )}
      {!isNullOrUndefined(newDashboardData.description) && (
        <TextInput {...commonProps('description')} label="Description" sx={{ maxWidth: '100%' }} />
      )}
      {newDashboardData.dashboardReportPermission?.viewers && (
        <Select
          muiProps={{ disableClearable: true, getOptionDisabled: (option: any) => option.disabled }}
          label={
            <>
              Viewers
              {accountDashboardAccessRoles.some(role => role.name === getRoleNameByValue(ROLES.ADMIN)) && (
                <Box sx={{ position: 'absolute', display: 'inline', ml: 0.5 }}>
                  <AvMessagePopover message="Admins can view all dashboards" icon={<QuestionFull />} asTooltip />
                </Box>
              )}
            </>
          }
          loading={isLoading}
          value={viewersValue}
          disabled={isViewersDisabled}
          onChange={v => {
            onChangeDashboardReportPermission('viewers')(
              v.includes(publicId) ? [publicId] : v.filter(id => !allowedToEditUsersSet.has(id))
            );
          }}
          isMultiple
          options={
            newDashboardData.dashboardReportPermission.viewers && newDashboardData.dashboardReportPermission.viewers.includes(publicId)
              ? [{ ...PublicOption, disabled: isViewersDisabled }]
              : usersOptionsForViewers
          }
          showClearSelection
          filterFunc={(options: any, { inputValue }) => options.filter(o => o.title.toLowerCase().includes(inputValue.toLowerCase()))}
          renderOption={({ title, iconFull }: any) => renderOption({ title, iconFull, sx: { p: '0' } })}
          renderOptionStyle={({ value }: any) =>
            value === publicId && !newDashboardData.dashboardReportPermission?.viewers.includes(publicId)
              ? { borderBottom: `1px solid var(--color-border-base-primary)` }
              : {}
          }
          style={selectCommonStyle}
        />
      )}
      {newDashboardData.dashboardReportPermission?.editors && (
        <Select
          muiProps={{ disableClearable: true }}
          label="Editors"
          loading={isLoading}
          value={newDashboardData.dashboardReportPermission.editors || []}
          onChange={v => {
            onChangeDashboardReportPermission('editors')(v.includes(publicId) ? [publicId] : v);
          }}
          isMultiple
          options={
            newDashboardData.dashboardReportPermission.editors && newDashboardData.dashboardReportPermission.editors.includes(publicId)
              ? [{ value: publicId, title: 'Public', iconFull: <Web style={iconSize(24)} /> }]
              : usersOptionsForEditors
          }
          showClearSelection
          filterFunc={(options: any, { inputValue }) => options.filter(o => o.title.toLowerCase().includes(inputValue.toLowerCase()))}
          renderOption={({ title, iconFull }: any) => renderOption({ title, iconFull, sx: { p: '0' } })}
          renderOptionStyle={({ value }: any) => (value === publicId ? { borderBottom: `1px solid var(--color-border-base-primary)` } : {})}
          style={selectCommonStyle}
        />
      )}
      {newDashboardData.apps && (
        <Select
          muiProps={{ disableClearable: true }}
          label="Pin to Apps"
          {...commonProps('apps')}
          isMultiple
          options={filteredAppsOption}
          renderOption={({ title, iconFull }: any) => renderOption({ title, iconFull, sx: { svg: { width: 22, height: 22 } } })}
          style={selectCommonStyle}
        />
      )}
      <DashboardsTagsSelect
        value={newDashboardData.tags || []}
        onChange={onChange('tags')}
        options={tagsOptions}
        style={selectCommonStyle}
      />
      {warning && (
        <Box sx={{ ...flex.row, gap: 1, mt: 1 }}>
          {orangeWarningIcon} <span>{warning}</span>
        </Box>
      )}
    </Box>
  );

  return anchorEl ? (
    <Popover
      sx={{ mt: 1.5 }}
      onClick={e => e.stopPropagation()}
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      onClose={onClose}>
      <Box sx={{ ...flex.col, width: 550, ...disabledStyle }}>
        <Box sx={{ px: 3, gap: 1, ...flex.col, pt: 2, pb: 6 }}>
          <Box sx={{ ...flex.justifyBetweenCenter, py: 1.5, pl: -1 }}>
            <Typography variant="h5">{`Edit ${title} Details`}</Typography>
            <Button onClick={onClose}>{exitIcon}</Button>
          </Box>
          {inputs}
        </Box>
      </Box>
    </Popover>
  ) : (
    <AvDialog
      fullWidth
      size="sm"
      open={isOpen}
      onClose={onClose}
      title={
        <Box sx={{ ...flex.justifyBetweenCenter, width: '100%', gap: 1 }}>
          {TitleGenerator[ModeType[modeType]]}
          {allowImportContent && (
            <Button size="xSmall" variant="outlined" onClick={handleAttachmentClick}>
              {exportIcon(undefined, { transform: 'rotate(180deg)' })} Import Template
            </Button>
          )}
        </Box>
      }
      footer={
        <Box sx={{ ...flex.row, ...disabledStyle }}>
          {modeType === ModeType.Share && (
            <CopyToClipboard
              title="Copy Dashboard Link"
              copyData={window.location.origin + window.location.pathname}
              copyType={CopyType.LINK}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ ...flex.justifyEndCenter, gap: 2 }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={onSave} disabled={isSaveDisabled}>
              {buttonText || ButtonGenerator[ModeType[modeType]]}
            </Button>
          </Box>
        </Box>
      }>
      {inputs}
    </AvDialog>
  );
};

export default EditDashboardDetailsWindow;
